import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    HostListener,
    inject,
    input,
    linkedSignal,
    model,
    output,
    Renderer2,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Invoice } from "../../pojo/Pricing";
import { PopoverComponent } from "../s25-popover/popover.component";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25DndSortableModule } from "../s25-dnd-sortable/s25.dnd.sortable.module";
import { S25Util } from "../../util/s25-util";
import { PricingService } from "../../services/pricing.service";
import { S25PricingStore } from "./s25.pricing.store";
import { S25PricingUtil } from "./s25.pricing.util";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { S25FadingSuccessMsgComponent } from "../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { Bind } from "../../decorators/bind.decorator";

@TypeManagerDecorator("pricing-sidebar")
@Component({
    selector: "pricing-sidebar",
    template: `
        <div class="action-bar">
            @if (sortChanged()) {
                <button class="c-textButton" [class.hidden]="!sidebarOpen()" (click)="saveSort()">
                    @if (savingSort()) {
                        <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                    } @else {
                        <s25-ng-icon [type]="'save'"></s25-ng-icon>
                    }
                    Save Sort
                </button>
            }
            @if (successMsg()) {
                <s25-ng-fading-success-msg [message]="successMsg"></s25-ng-fading-success-msg>
            }
            <s25-ng-icon class="menu-button" [type]="'doubleArrowLeft'" (click)="pivotSidebar()"></s25-ng-icon>
        </div>
        <ul
            s25-ng-dnd-sortable
            [class.hidden]="!sidebarOpen()"
            [items]="invoices()"
            [sortable]="canSort()"
            (orderChanged)="onOrderChanged($event)"
        >
            <li
                [class.active]="chosenInvoice().id === defaultViewId()"
                (click)="togglePricingSet.emit({ id: defaultViewId() })"
                (keydown.enter)="togglePricingSet.emit({ id: defaultViewId() })"
            >
                <a>{{ defaultViewName() }}</a>
            </li>
            @for (invoice of invoices(); track invoice.id) {
                <li
                    s25-ng-dnd-sortable-item
                    [index]="$index"
                    [draggable]="canSort()"
                    [class.active]="invoice.id === chosenInvoice().id"
                    (click)="togglePricingSet.emit(invoice)"
                    (keydown.enter)="togglePricingSet.emit(invoice)"
                >
                    <a [class.sortable]="canSort()">
                        <s25-popover
                            [popoverTemplate]="'Invoices are now accessed in Payment Mode'"
                            [openTrigger]="'click'"
                            [closeTrigger]="'click'"
                            [disabled]="paymentsMode()"
                        >
                            @if (canSort()) {
                                <s25-ng-icon class="drag-handle" [type]="'dragHandle'"></s25-ng-icon>
                            }
                            <p>
                                {{ invoice.billDefn.billName }}
                            </p>
                            @if (canMakeChanges()) {
                                <s25-ng-icon-button
                                    [type]="'edit'"
                                    [size]="20"
                                    (click)="$event.stopPropagation(); this.editNameIndex.set($index)"
                                ></s25-ng-icon-button>
                            }
                        </s25-popover>
                    </a>
                    @if (editNameIndex() === $index) {
                        <div #nameForm class="name-form" (click)="$event.stopPropagation()">
                            <input
                                class="c-input"
                                [value]="invoice.billDefn.billName"
                                (input)="inputVal.set($any($event.target).value)"
                                maxlength="80"
                            />
                            @if (nameSaveError()) {
                                <p class="ngRed">{{ nameSaveError() }}</p>
                            }
                            <div class="aw-button-group">
                                <button
                                    class="aw-button aw-button--outline"
                                    (click)="$event.stopPropagation(); editNameIndex.set(null)"
                                >
                                    Cancel
                                </button>
                                <s25-ng-button [type]="'primary'" [onClick]="saveName" [disabled]="disableNameSave()"
                                    >Save</s25-ng-button
                                >
                            </div>
                        </div>
                    }
                </li>
            }
        </ul>
    `,
    host: {
        class: "sidebar-menu hide-on-mobile pricing-sidebar",
        "[class.closed]": "!sidebarOpen()",
    },
    styles: `
        :host {
            margin-top: 1em;
            min-width: 0;
            flex: 0 0 200px;
            transition: flex-basis 1s;

            &.closed {
                flex: 0 0 2.25em;
            }

            .action-bar {
                display: flex;
                justify-content: flex-end;
                margin: 5px;

                .c-textButton {
                    position: relative;
                    top: 1px;
                    color: #fff !important;
                    opacity: 1;
                    transition: opacity 1.5s ease-in;
                    margin-right: auto;

                    &.hidden {
                        opacity: 0;
                        transition: opacity 0.3s;
                    }
                }

                s25-ng-fading-success-msg {
                    margin-right: auto;
                }
            }

            ul {
                padding-top: 0;
                opacity: 1;
                transition: opacity 1.5s ease-in;

                &.hidden {
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                s25-ng-icon-button {
                    display: none;
                    color: #2573a7;
                }

                li {
                    position: relative;

                    &:hover,
                    &:focus {
                        s25-ng-icon-button {
                            display: unset;
                        }
                    }

                    .name-form {
                        position: absolute;
                        background-color: #fff;
                        z-index: 11;
                        padding: 10px;
                        left: 1.5em;
                        box-shadow:
                            rgba(0, 0, 0, 0.16) 0 10px 36px 0,
                            rgba(0, 0, 0, 0.06) 0 0 0 1px;
                        border-radius: 4px;

                        .ngRed {
                            font-size: 0.9rem;
                        }

                        .aw-button-group {
                            display: flex;
                            justify-content: space-evenly;
                            padding-left: 0;
                        }
                    }

                    &:not(.active) {
                        .sortable {
                            s25-ng-icon {
                                filter: brightness(0) invert(1);
                            }

                            &:hover {
                                s25-ng-icon {
                                    filter: unset;
                                }
                            }
                        }
                    }

                    s25-popover {
                        width: 100%;
                        display: flex !important;
                        justify-content: space-between;
                    }

                    .sortable {
                        padding-left: 0;

                        s25-popover {
                            gap: 5px;
                            padding-left: 5px;

                            .drag-handle {
                                font-size: 1.5em;

                                &:hover {
                                    cursor: move;
                                }
                            }

                            p {
                                flex: 1 0 6.5em;
                            }

                            s25-ng-icon-button {
                                margin-left: auto;
                            }
                        }
                    }
                }
            }
        }

        :host ::ng-deep ~ div {
            margin-left: unset;
            padding-left: unset;
        }

        ::ng-deep .pricing-sidebar .menu-button svg {
            transition: transform 0.5s;
            filter: invert(100%) sepia(0%) saturate(7492%) hue-rotate(145deg) brightness(101%) contrast(106%);
        }

        ::ng-deep .pricing-sidebar .menu-button svg.rotate {
            transform: rotate(180deg);
        }

        ::ng-deep .pricing-sidebar .menu-button svg:hover {
            cursor: pointer;
        }

        ::ng-deep .pricing-sidebar s25-ng-loading-inline-static circle {
            stroke: #fff;
        }

        ::ng-deep .pricing-sidebar s25-ng-loading-inline-static .s25-text {
            display: none;
        }

        ::ng-deep .pricing-sidebar s25-ng-fading-success-msg .success-msg {
            color: #fff;
        }

        ::ng-deep .pricing-sidebar s25-ng-icon-button svg {
            color: #2573a7;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PopoverComponent,
        S25IconComponent,
        S25DndSortableModule,
        S25LoadingInlineModule,
        S25FadingSuccessMsgComponent,
        S25IconButtonComponent,
        S25ButtonComponent,
    ],
})
export class PricingSidebarComponent {
    eventId = input<number>();
    chosenInvoice = input<Invoice>();
    paymentsMode = input<boolean>();
    canEdit = input<boolean>();
    combineRelated = input<boolean>();
    sidebarOpen = model<boolean>();

    canMakeChanges = computed(() => this.canEdit() && this.paymentsMode());
    canSort = computed(
        () => this.canMakeChanges() && this.invoices().length > 1 && !this.editNameIndex() && !this.combineRelated(),
    );
    sortChanged = computed(
        () =>
            !S25Util.array.isEqual(
                this.invoices().map((invoice) => invoice.id),
                this.originalSortOrderById(),
            ),
    );
    defaultViewId = computed(() =>
        this.paymentsMode() ? S25PricingUtil.SummaryViewId : S25PricingUtil.StandardPricingId,
    );
    defaultViewName = computed(() => (this.paymentsMode() ? "Invoice Activity" : "Standard Pricing"));

    private pricingStore = inject(S25PricingStore);
    eventInvoices = computed(() => this.pricingStore.invoices());
    invoices = linkedSignal(() => this.eventInvoices());

    originalSortOrderById = signal(this.invoices().map((inv) => inv.id));
    savingSort = signal(false);
    successMsg = signal<string>(null);
    editNameIndex = signal<number>(null);

    nameForm = viewChild<ElementRef>("nameForm");
    inputVal = signal("");
    disableNameSave = computed(() => {
        const invoice = this.invoices()[this.editNameIndex()],
            name = this.inputVal();
        return !name.length || name === invoice?.billDefn?.billName;
    });
    nameSaveError = signal("");

    togglePricingSet = output<Invoice>();

    @HostListener("document:click", ["$event"])
    handleClick(event: MouseEvent) {
        const formEl = this.nameForm();
        if (formEl && !formEl.nativeElement.contains(event.target)) this.editNameIndex.set(null);
    }

    constructor(
        private renderer: Renderer2,
        private elementRef: ElementRef,
    ) {}

    pivotSidebar() {
        this.sidebarOpen.update((prev) => !prev);
        const icon = this.elementRef.nativeElement.querySelector(".sidebar-menu svg");
        if (this.sidebarOpen()) {
            this.renderer.removeClass(icon, "rotate");
        } else {
            this.renderer.addClass(icon, "rotate");
        }
    }

    onOrderChanged(invoices: Invoice[]) {
        // drag/drop directive mutates the array, so need to manually reassign signal for changes to reflect
        this.invoices.set([...invoices]);
    }

    async saveSort() {
        this.savingSort.set(true);
        const payload = this.invoices().map((inv, index) => ({
            evBillId: inv.id,
            sortOrder: index + 1,
            created: inv.billDefn.created,
        }));
        const [_, error] = await S25Util.Maybe(PricingService.putInvoiceSortOrder(this.eventId(), payload));
        this.savingSort.set(false);
        if (error) return S25Util.showError(error);
        this.successMsg.set("Saved!");
        this.originalSortOrderById.set(this.invoices().map((inv) => inv.id));
    }

    @Bind
    async saveName() {
        const invoice = this.invoices()[this.editNameIndex()],
            name = this.inputVal();
        if (invoice?.id && name) {
            const [_, error] = await S25Util.Maybe(PricingService.putBillingCustom({ billName: name }, invoice.id));
            if (error) {
                this.nameSaveError.set(S25Util.errorText(error));
                return console.error(error);
            }
            this.pricingStore.updateInvoiceName(invoice.id, name);
            this.editNameIndex.set(null);
            this.nameSaveError.set("");
        }
    }
}
